import { Environment } from '@x/common/environment';
import { streamlineRegular } from '@x/common/icon';
import { GOOGLE_MAPS_CONFIG } from '@x/ecommerce-shop/config/map-config';
import { IShopConfig } from '@x/ecommerce-shop/config/shop.config';
import { BABYLONSTOREN_BRAND } from '@x/ecommerce-shop/flavours/babylonstoren/config/brand';

export const getShopConfig = (env: Environment): IShopConfig => ({
  production: env.requireBool('NG_PRODUCTION'),
  flavor: 'bt',
  brand: BABYLONSTOREN_BRAND,
  defaultLocale: 'en-ZA',
  meta: {
    titleSuffix: BABYLONSTOREN_BRAND.name,
    defaultMeta: {
      metaTitle: 'Welcome',
      metaDescription:
        'Our online shop is a reflection of our contemporary, unhurried farm lifestyle and brings the best to your doorstep.',
      metaImage: `https://res.cloudinary.com/${env.requireStr('NG_CLOUDINARY')}/image/fetch/v1662362337/https://storage.googleapis.com/mediabucket-prod-00fecdb9/babylonstoren/image/053e18c89e99c7d72a3a3a35911410f67db8.jpg`,
    },
  },
  cloudinary: {
    cloudName: env.requireStr('NG_CLOUDINARY'),
  },
  ngxs: {
    developmentMode: !env.requireBool('NG_PRODUCTION'),
  },
  defaultCountry: {
    name: 'South Africa',
    dialCode: '27',
    code: 'ZA',
  },
  ecommerceBackend: {
    baseUrl: env.requireStr('NG_APP_URL') + '/ecommerce/backend',
  },
  ecommerceShopClient: {
    graphUrl: env.requireStr('NG_APP_URL') + '/ecommerce/graphql',
  },
  contentClient: {
    graphUrl: env.requireStr('NG_APP_URL') + '/content/graphql',
  },
  geocodeClient: {
    graphUrl: env.requireStr('NG_APP_URL') + '/geocode/graphql',
  },
  money: {
    format: {
      currencyDisplay: 'narrowSymbol',
      // only available in newer browsers
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#trailingzerodisplay
      trailingZeroDisplay: 'stripIfInteger',
    } as any,
  },
  contactInfo: {
    email: 'deliveries@babylonstoren.com',
    phone: '+27 (0) 21 300 3919',
    whatsapp: {
      number: '+27 (79) 996 1910',
      link: 'https://wa.me/27799961910',
    },
    availability: 'Available Monday - Saturday from 07h00 - 18h00',
  },
  barcode: {
    url: 'https://europe-west1-ecommerce-283509.cloudfunctions.net/x-barcode-svg-stage',
  },
  date: {
    format: 'd MMM, y',
  },
  iconConfig: {
    fixedWidth: true,
    defaultPrefix: 'fal',
    packs: [streamlineRegular],
  },
  storageConfig: { prefix: 'x_ecommerce_shop_', version: 1 },
  googleMaps: GOOGLE_MAPS_CONFIG,
  features: {
    availability: {
      showUnavailableSlots: true,
      showDeliveryRegionBadges: true,
      availabilityStatus: {
        AVAILABLE_SOON: {
          minDays: 3,
        },
        PRE_ORDER: {
          minDays: 14,
        },
        LOW_STOCK_URGENCY: {
          maxDays: 7,
          maxQuantity: 4,
        },
        LAST_CHANCE_AVAILABILITY: {
          maxDays: 7,
        },
      },
    },
    productListingViewMoreMode: 'simple',
    productViewMobileStickyActionBar: true,
    deliverySlotCalendar: true,
    enableNeedHelpChat: false,
    newsletterOptIn: {
      onRegistration: true,
      enabledByDefault: true,
      text: 'I would like to receive communication from Babylonstoren. ( View our Privacy Policy )',
      policy: {
        absoluteUrl: 'https://babylonstoren.com/privacy-policy',
        textToMatch: 'Privacy Policy',
      },
    },

    showRegionOverlayTaxons: ['farm-fresh'],
    mainMenu: {
      taxonTargetLevel: [0, 1],
    },
    menuTree: {
      taxonTargetLevel: [0],
    },
  },
  tracking: {
    algolia: {
      appId: env.requireStr('NG_ALGOLIA_APP_ID'),
      apiKey: env.requireStr('NG_ALGOLIA_API_KEY'),
    },
    logRocket: {
      appId: env.requireStr('NG_LOGROCKET'),
    },
    googleTagManager: {
      containerId: 'GTM-57C35KP',
    },
    fireWorks: {
      analyticsSrcUrl: 'https://asset.fwcdn3.com/js/analytics.js',
    },
  },
  abTestConfig: {
    apiHost: env.optStr('NG_GROWTH_BOOK_API_HOST'),
    clientKey: env.optStr('NG_GROWTH_BOOK_CLIENT_KEY'),
    enableDevMode: env.bool('NG_GROWTH_BOOK_ENABLE_DEBUG', undefined),
    features: {
      'product-detail-recommendations': {
        name: 'product-detail-recommendations-bt',
        options: {
          showByDefault: true,
          allowNull: true,
          firstValueOnly: true,
        },
      },
      'payment-options': {
        name: 'ecommerce-payment-options-bt',
        options: {
          showByDefault: false,
        },
      },
    },
  },
  browser: {
    cookies: {
      prefix: 'bt_',
      domain: 'localhost',
      secure: true,
      path: '/',
    },
    scripts: [
      {
        name: 'google-maps',
        src: 'https://maps.googleapis.com/maps/api/js?key=AIzaSyCxKH3-upo3yLjFguZvJufUZ5P-qxN2fhs&callback=Function.prototype&loading=async',
        defer: true,
        async: true,
      },
      {
        name: 'story-block',
        src: [
          'https://asset.fwcdn3.com/js/storyblock.js',
          'https://asset.fwcdn3.com/js/fwn.js',
          'https://asset.fwcdn3.com/js/embed-feed.js',
        ],
        defer: true,
        async: true,
      },
      // {
      //   name: 'robin-chat',
      //   src: 'https://selfservice.robinhq.com/external/robin/49o098gm.js',
      //   defer: true,
      //   async: false,
      // },
    ],
  },
});
